import posthog from "posthog-js";
import { datadogRum } from "@datadog/browser-rum";
import {
  POSTHOG_DISTINCT_ID,
  POSTHOG_SESSION_ID,
} from "../constants/posthogEvents";
import { posthogFeatureFlagService } from "./posthogFeatureFlagService";

export { posthog };

let hasPosthogInitStarted = false;
let isPosthogSdkReady = false;

export const isPosthogReady = (): boolean => {
  return isPosthogSdkReady;
};

export const initializePosthog = (): void => {
  if (hasPosthogInitStarted) {
    return;
  }
  hasPosthogInitStarted = true;
  isPosthogSdkReady = false;

  let resolvedDistinctId: string | null = null;
  let resolvedSessionId: string | null = null;

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlDistinctId = urlParams.get(POSTHOG_DISTINCT_ID);
    const urlSessionId = urlParams.get(POSTHOG_SESSION_ID);

    if (urlDistinctId) {
      resolvedDistinctId = urlDistinctId;
      if (import.meta.env.MODE !== "production") {
        console.log(`PostHog: Found distinct_id in URL: ${resolvedDistinctId}`);
      }
    }

    if (urlSessionId) {
      resolvedSessionId = urlSessionId;
      if (import.meta.env.MODE !== "production") {
        console.log(`PostHog: Found session_id in URL: ${resolvedSessionId}`);
      }
    }
  } catch (error) {
    console.error("Failed to check URL params for PostHog IDs", error);
  }

  try {
    if (!resolvedDistinctId && typeof sessionStorage !== "undefined") {
      const storedDistinctId = sessionStorage.getItem(POSTHOG_DISTINCT_ID);
      if (storedDistinctId) {
        resolvedDistinctId = storedDistinctId;
        if (import.meta.env.MODE !== "production") {
          console.log(
            `PostHog: Using distinct_id from sessionStorage: ${resolvedDistinctId}`
          );
        }
      }
    }

    if (!resolvedSessionId && typeof sessionStorage !== "undefined") {
      const storedSession = sessionStorage.getItem(POSTHOG_SESSION_ID);
      if (storedSession) {
        resolvedSessionId = storedSession;
        if (import.meta.env.MODE !== "production") {
          console.log(
            `PostHog: Using session_id from sessionStorage: ${resolvedSessionId}`
          );
        }
      }
    }

    if (resolvedDistinctId && typeof sessionStorage !== "undefined") {
      const currentStored = sessionStorage.getItem(POSTHOG_DISTINCT_ID);
      if (currentStored !== resolvedDistinctId) {
        sessionStorage.setItem(POSTHOG_DISTINCT_ID, resolvedDistinctId);
      }
    }

    if (resolvedSessionId && typeof sessionStorage !== "undefined") {
      const currentStored = sessionStorage.getItem(POSTHOG_SESSION_ID);
      if (currentStored !== resolvedSessionId) {
        sessionStorage.setItem(POSTHOG_SESSION_ID, resolvedSessionId);
      }
    }
  } catch (error) {
    console.error("Failed to read/write sessionStorage for PostHog IDs", error);
  }

  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    ...((resolvedDistinctId || resolvedSessionId) && {
      bootstrap: {
        ...(resolvedDistinctId && { distinctID: resolvedDistinctId }),
        ...(resolvedSessionId && { sessionID: resolvedSessionId }),
      },
    }),
    ui_host: "https://us.i.posthog.com",
    autocapture: false,
    save_campaign_params: true,
    save_referrer: true,
    disable_session_recording: false,
    request_batching: false,
    person_profiles: "always",
    persistence: "localStorage+cookie",
    disable_persistence: false,
    cross_subdomain_cookie: true,
    loaded: (api) => {
      isPosthogSdkReady = true;
      if (!resolvedDistinctId) {
        resolvedDistinctId = api.get_distinct_id();
        if (import.meta.env.MODE !== "production") {
          console.log(
            `PostHog: Using distinct_id from localStorage/cookies: ${resolvedDistinctId}`
          );
        }
      }

      if (!resolvedSessionId) {
        resolvedSessionId = api.get_session_id();
        if (import.meta.env.MODE !== "production") {
          console.log(
            `PostHog: Using session_id from localStorage/cookies: ${resolvedSessionId}`
          );
        }
      }

      const distinctId = resolvedDistinctId ?? api.get_distinct_id();
      const sessionId = resolvedSessionId ?? api.get_session_id();

      resolvedDistinctId = distinctId;
      resolvedSessionId = sessionId;

      try {
        if (typeof sessionStorage !== "undefined") {
          const currentStoredDistinctId =
            sessionStorage.getItem(POSTHOG_DISTINCT_ID);
          if (currentStoredDistinctId !== distinctId) {
            sessionStorage.setItem(POSTHOG_DISTINCT_ID, distinctId);
          }

          const currentStoredSessionId =
            sessionStorage.getItem(POSTHOG_SESSION_ID);
          if (currentStoredSessionId !== sessionId) {
            sessionStorage.setItem(POSTHOG_SESSION_ID, sessionId);
          }
        }
      } catch (error) {
        console.error("Failed to persist PostHog IDs to sessionStorage", error);
      }

      api.identify(distinctId);
      if (import.meta.env.MODE !== "production") {
        // Use a single formatted string so Playwright `msg.text()` includes the id
        // (e2e tests assert on this log containing the distinct id).
        console.log(`PostHog: Identified with distinct_id: ${distinctId}`);
      }

      // Add Datadog session context to PostHog
      let registeredDdSessionId: string | null = null;
      const syncDatadogToPostHog = (): boolean => {
        try {
          const ddContext = datadogRum.getInternalContext();
          const ddSessionId = ddContext?.session_id;
          if (!ddSessionId) return false;

          if (registeredDdSessionId === ddSessionId) return true;

          // Get configured Datadog site (e.g., "us5.datadoghq.com")
          const ddConfig = datadogRum.getInitConfiguration();
          const ddSite = ddConfig?.site || "datadoghq.com";
          const ddSessionUrl = `https://${ddSite}/rum/sessions/${ddSessionId}`;

          api.register({
            dd_session_id: ddSessionId,
            dd_session_url: ddSessionUrl,
          });

          registeredDdSessionId = ddSessionId;
          if (import.meta.env.MODE !== "production") {
            console.log("PostHog: Datadog context added successfully");
          }
          return true;
        } catch (error) {
          return false;
        }
      };

      // Retry if Datadog isn't ready yet.
      if (!syncDatadogToPostHog()) {
        const maxAttempts = 20;
        const intervalMs = 500;
        let attempts = 0;
        const intervalId = window.setInterval(() => {
          attempts += 1;
          const done = syncDatadogToPostHog();
          if (done || attempts >= maxAttempts) {
            window.clearInterval(intervalId);
          }
        }, intervalMs);
      }

      const syncPostHogToDatadog = (
        distinctToSet: string,
        sessionToSet: string
      ): boolean => {
        try {
          if (!datadogRum.getInitConfiguration()) return false;
          datadogRum.setGlobalContextProperty(
            "posthog_distinct_id",
            distinctToSet
          );
          datadogRum.setGlobalContextProperty(
            "posthog_session_id",
            sessionToSet
          );
          if (import.meta.env.MODE !== "production") {
            console.log("PostHog: Added IDs to Datadog global context");
          }
          return true;
        } catch (error) {
          return false;
        }
      };

      // Retry if Datadog init hasn't happened yet.
      if (!syncPostHogToDatadog(distinctId, sessionId)) {
        const maxAttempts = 20;
        const intervalMs = 500;
        let attempts = 0;
        const intervalId = window.setInterval(() => {
          attempts += 1;
          const done = syncPostHogToDatadog(distinctId, sessionId);
          if (done || attempts >= maxAttempts) {
            window.clearInterval(intervalId);
          }
        }, intervalMs);
      }

      // Listen for identity changes (login/logout/alias)
      // Note: PostHog JS SDK supports api.on('identify') and api.on('reset')
      // See: https://posthog.com/docs/libraries/js
      if (typeof api.on === "function") {
        // @ts-expect-error - PostHog types don't include 'identify' event but it exists at runtime
        api.on("identify", (newDistinctId: string) => {
          try {
            const newSessionId = api.get_session_id();
            resolvedDistinctId = newDistinctId;
            resolvedSessionId = newSessionId;

            const updated = syncPostHogToDatadog(newDistinctId, newSessionId);
            if (updated) {
              datadogRum.addAction("posthog_identity_change", {
                new_distinct_id: newDistinctId,
                new_session_id: newSessionId,
                timestamp: Date.now(),
              });
            } else {
              // If Datadog isn't ready yet, syncPostHogToDatadog has its own retry logic elsewhere.
              // Do a best-effort immediate action payload only once Datadog is ready.
              const maxAttempts = 10;
              const intervalMs = 500;
              let attempts = 0;
              const intervalId = window.setInterval(() => {
                attempts += 1;
                const done = syncPostHogToDatadog(newDistinctId, newSessionId);
                if (done || attempts >= maxAttempts) {
                  if (done) {
                    datadogRum.addAction("posthog_identity_change", {
                      new_distinct_id: newDistinctId,
                      new_session_id: newSessionId,
                      timestamp: Date.now(),
                    });
                  }
                  window.clearInterval(intervalId);
                }
              }, intervalMs);
            }
          } catch (error) {
            console.warn(
              "Failed to update Datadog on PostHog identify:",
              error
            );
          }
        });

        // @ts-expect-error - PostHog types don't include 'reset' event but it exists at runtime
        api.on("reset", () => {
          try {
            if (datadogRum.getInitConfiguration()) {
              datadogRum.setGlobalContextProperty("posthog_distinct_id", null);
              datadogRum.setGlobalContextProperty("posthog_session_id", null);
              datadogRum.addAction("posthog_session_reset", {
                timestamp: Date.now(),
              });
            }
          } catch (error) {
            console.warn("Failed to update Datadog on PostHog reset:", error);
          }
        });
      } else {
        // Fallback: poll for identity changes if event API not available
        let lastDistinctId = distinctId;
        let lastSessionId = sessionId;
        const pollInterval = setInterval(() => {
          try {
            const currentId = api.get_distinct_id();
            const currentSessionId = api.get_session_id();

            if (
              currentId !== lastDistinctId ||
              currentSessionId !== lastSessionId
            ) {
              syncPostHogToDatadog(currentId, currentSessionId);
              lastDistinctId = currentId;
              lastSessionId = currentSessionId;
            }
          } catch (error) {
            console.warn("Failed to poll PostHog identity:", error);
          }
        }, 5000);

        // Store interval ID for potential cleanup
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__posthogIdentityPollInterval = pollInterval;
      }

      api.capture("funnel_page_view", {
        distinct_id: distinctId,
        session_id: sessionId,
        url: window.location.href,
        referrer: document.referrer,
      });
      api.setPersonPropertiesForFlags({
        distinct_id: distinctId,
        session_id: sessionId,
      });
      api.setPersonProperties({
        funnel_source: "mega-funnel",
      });

      if (typeof api.onFeatureFlags === "function") {
        api.onFeatureFlags((flags, _variants, context) => {
          try {
            posthogFeatureFlagService.handlePosthogPayload({
              flags: Array.isArray(flags) ? flags : undefined,
              errorsLoading: Boolean(context?.errorsLoading),
              evaluator: api.isFeatureEnabled.bind(api),
            });
          } catch (error) {
            console.error(
              "Failed to process PostHog feature flag payload",
              error
            );
            datadogRum.addError(
              error instanceof Error
                ? error
                : new Error("Failed to process PostHog feature flag payload"),
              { source: "posthog_feature_flag_handler" }
            );
          }
        });
      } else {
        console.warn(
          "PostHog: onFeatureFlags API is unavailable. Feature flags will use defaults."
        );
        datadogRum.addAction("posthog_feature_flag_handler_missing", {});
      }
    },
  });
};
